import { useConfig } from "../../providers/AppStore";
import { LocalStorageVariables } from "../entities/type";
import axios from "axios";
import ENDPOINTS from "./Endpoints";
import * as Sentry from "@sentry/react";
import { AffiliateUserType, UserType } from "../entities/profile";
import {
  getCustomerSession,
  setCustomerSession,
  updateCustomerToken,
} from "./customerSession";
import { isLoggingOut, runSessionTeardowns } from "../utils/sessionTeardown";
import { rememberIdentity } from "../utils/affiliateIdentity";
import {
  isAuthPath,
  rememberRedirectUrl,
  shouldSkipRedirectUrl,
} from "../utils/redirectUrl";

// Grace period before a dead-session redirect, so the rejection this interceptor
// is about to raise can reach the callers that report diagnostics, and Sentry can
// flush them (Replay only uploads its buffer on flush). Without it, assigning
// window.location.href cut both off — which is why the 401 anomaly events arrived
// with no user and no replay attached.
const DEAD_SESSION_REDIRECT_GRACE_MS = 300;

const AppApiInstance = async (
  baseUrlType: string,
  userAuthorization: boolean,
  affiliateForCostumerAuthorization: boolean
) => {
  let CONFIG = await useConfig();
  const userValue = await localStorage.getItem(LocalStorageVariables.USER);

  let usr: UserType | AffiliateUserType | undefined;
  if (userValue) {
    usr = JSON.parse(userValue);
    // Record who this is while storage still has it — a dead session gets cleared
    // below, before any anomaly reporter gets to look (see affiliateIdentity).
    rememberIdentity(usr);
  }

  // Customer session is per-tab (sessionStorage) so multiple tabs can hold
  // different clients without sharing the customer token.
  const customerUsr: UserType | undefined = getCustomerSession() ?? undefined;

  if (userAuthorization && !usr?.token && !shouldSkipRedirectUrl()) {
    console.log(
      "🔴 AppApiManager: No token, setting redirect URL:",
      window.location.pathname
    );
    // If the user is not authorized, save where they were and send them to
    // log in (see redirectUrl for the write policy).
    rememberRedirectUrl();
    // Redirect to login page
    window.location.href = "/login";
  }

  if (
    affiliateForCostumerAuthorization &&
    !customerUsr?.token &&
    !isLoggingOut()
  ) {
    console.log(
      "authorized endpoint from affiliate for customer data with no token"
    );
    window.location.href = "/login";
  }

  const getEnvFromDomain = (): string => {
    const hostname = window.location.hostname;

    switch (hostname) {
      case "welcome.mypnoe.com":
      case "welcome-staging.mypnoe.com":
        return "prod";
      case "welcome-dev.mypnoe.com":
        return "dev";
      case "localhost":
      default:
        return "dev";
    }
  };

  const instance = axios.create({
    baseURL: `${CONFIG[baseUrlType as keyof typeof CONFIG]}`,
    headers: {
      ...(baseUrlType !== "PNOE_API_BASE_URL" && {
        "Content-Type": "application/json; charset=utf-8",
      }),
      ...(baseUrlType !== "PNOE_API_BASE_URL" && {
        Accept: "application/json",
      }),
      "X-GO": "1",
      "Accept-Version": "1.0.0",
      "Action-Source": "welcome",
      //add env header if needed
      ...(baseUrlType === "PLATFORM_BASE_URL" && {
        "X-Environment": getEnvFromDomain(),
      }),
      // add authorization header if needed
      ...(userAuthorization &&
        usr?.token && { Authorization: "Bearer " + usr?.token }),
      ...(affiliateForCostumerAuthorization &&
        customerUsr?.token && {
          Authorization: "Bearer " + customerUsr?.token,
        }),
    },
  });

  instance.interceptors.request.use(
    (x) => {
      const headers = {
        ...x.headers.common,
        ...(x.method && x.headers[x.method]),
        ...x.headers,
      };
      // remove irrelevant headers to reduce the noise
      ["common", "get", "post", "head", "put", "patch", "delete"].forEach(
        (header) => {
          delete headers[header];
        }
      );

      const printable = `REQUEST | ${new Date().toISOString()} | Request: ${
        x.method
      } | ${x.url} | ${JSON.stringify(x.data)} | ${JSON.stringify(x.headers)}`;

      // console.error(printable);

      return x;
    },
    function (error) {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      console.log("error expire token", error.response?.data);

      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (x) => {
      return x;
    },

    async function (error) {
      const originalRequest = error.config;
      // If the error status is 401 and there is no originalRequest._retry flag,
      // it means the token has expired and we need to refresh it
      console.log("Api Call is Rejected! Error Message ->", error);
      // console.log(
      //   "Api Call is Rejected! Error Message ->",
      //   JSON.stringify(error.response.data)
      // );
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        // Not every 401 is an expired session, and only an expired session may be
        // refreshed. Bail out (plain rejection, no refresh, no redirect) when:
        //
        // - This instance carries no session at all — the login endpoint itself
        //   goes through PlatformBaseApiNoAuth, so a wrong password lands here. A
        //   refresh with an undefined token fails, and reacting to that failure
        //   would reload the page out from under the sign-in form and discard the
        //   "Incorrect username or password" message.
        // - There is no refresh token to spend — checked on the identity that
        //   actually made the request (see refreshSource), not always on `usr`:
        //   a customer-token request is refreshed with the customer's own
        //   credentials, so the affiliate having a refresh token proves nothing.
        // - The session was cleared while the request was in flight: logout wipes
        //   localStorage and then assigns window.location.href, which does not stop
        //   pending requests. Refreshing would write the token back and resurrect
        //   the user that was just deleted.
        //
        // Refresh with the identity that made the request: customer-token
        // requests must refresh with the customer's own credentials — the
        // refreshed token replaces the token it was minted from.
        const refreshSource = affiliateForCostumerAuthorization
          ? customerUsr
          : usr;

        const hasSessionToRefresh =
          (userAuthorization || affiliateForCostumerAuthorization) &&
          !!refreshSource?.refresh_token;

        if (
          !hasSessionToRefresh ||
          isLoggingOut() ||
          !localStorage.getItem(LocalStorageVariables.USER)
        ) {
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(
            `${CONFIG["ID_BASE_URL"]}${ENDPOINTS.ID.REFRESH_TOKEN_URL}`,
            {
              id: refreshSource?.identity,
              refresh_token: refreshSource?.refresh_token,
            }
          );
          const { id_token } = response.data;

          // Persist the refreshed token onto whatever storage holds *now*, not
          // onto the `usr`/`customerUsr` snapshots taken when this instance was
          // created. If the session was cleared while the request was in flight,
          // the key is gone and writing the snapshot back would resurrect it; if
          // another request already refreshed, the snapshot is stale. Either way,
          // bail rather than reviving cleared auth state.
          const persistRefreshedToken = (
            key: string,
            snapshot: UserType | AffiliateUserType
          ): boolean => {
            const liveValue = localStorage.getItem(key);
            if (!liveValue) return false;

            const liveUsr = JSON.parse(liveValue);
            liveUsr.token = id_token;
            localStorage.setItem(key, JSON.stringify(liveUsr));
            // Keep the closure in sync so retries on this instance use the new token
            snapshot.token = id_token;
            return true;
          };

          if (affiliateForCostumerAuthorization && customerUsr) {
            // The customer session is per-tab sessionStorage, so it gets the
            // same live-read treatment via customerSession: updateCustomerToken
            // re-reads the tab's session and reports false when it is gone.
            if (!updateCustomerToken(id_token)) {
              return Promise.reject(error);
            }
            customerUsr.token = id_token;
          } else if (userAuthorization && usr) {
            if (!persistRefreshedToken(LocalStorageVariables.USER, usr)) {
              return Promise.reject(error);
            }
          }

          originalRequest.headers.Authorization = `Bearer ${id_token}`;
          return axios(originalRequest);
        } catch (e) {
          console.log("error in refresh token request", e);
          // The refresh token itself was rejected — the session is dead and every
          // subsequent request will 401, leaving the user on a broken page. Clear
          // auth state (same keys as AuthProvider.logout, which isn't reachable
          // from here), remember where they were, and send them to login. Network
          // failures fall through: a flaky refresh call shouldn't log anyone out.
          const refreshStatus = axios.isAxiosError(e)
            ? e.response?.status
            : undefined;
          if (
            refreshStatus !== undefined &&
            [400, 401, 403].includes(refreshStatus)
          ) {
            localStorage.removeItem(LocalStorageVariables.USER);
            // This tab's client (sessionStorage) plus the legacy localStorage
            // copy, for sessions started before the per-tab migration. Other
            // tabs clear their own in the logout-event listener below.
            setCustomerSession(null);
            localStorage.removeItem(LocalStorageVariables.COSTUMER);
            localStorage.removeItem(LocalStorageVariables.NOTES);
            localStorage.removeItem(LocalStorageVariables.ENVIRONMENT);
            localStorage.removeItem(LocalStorageVariables.HISTORY_FILTERS);
            localStorage.removeItem(LocalStorageVariables.TIMELINE_PREFERENCES);
            // In-memory caches too: this branch may stay on the sign-in page
            // without a reload, and the next user must not see the last one's.
            runSessionTeardowns();
            rememberRedirectUrl();
            // Broadcast logout to other open tabs (see AuthProvider storage listener)
            localStorage.setItem("logout-event", Date.now().toString());
            localStorage.removeItem("logout-event");
            // Already on the sign-in screen: the storage above is enough. Reloading
            // would wipe whatever the form is showing (e.g. an error message).
            if (!isAuthPath(window.location.pathname)) {
              // Deferred, then flushed: the rejection returned below still has to
              // propagate to callers that capture a diagnostic event, and Replay
              // uploads its buffer only on flush. flush() resolves immediately when
              // Sentry isn't initialised (dev/local), so this costs one tick there.
              const redirectToLogin = () => {
                window.location.href = "/login";
              };
              setTimeout(() => {
                Sentry.flush(2000).then(redirectToLogin, redirectToLogin);
              }, DEAD_SESSION_REDIRECT_GRACE_MS);
            }
          }
        }
      }

      return Promise.reject(error);
    }
  );
  return instance;
};

export const IDBaseApi = async () =>
  await AppApiInstance("ID_BASE_URL", false, false);

export const NUTBaseApi = async () =>
  await AppApiInstance("NUT_BASE_URL", true, false);

export const NUTBaseApiNoAuth = async () =>
  await AppApiInstance("NUT_BASE_URL", false, false);

export const NUTBaseApiAffiliateRequest = async () =>
  await AppApiInstance("NUT_BASE_URL", false, true);

export const PlatformBaseApi = async () =>
  await AppApiInstance("PLATFORM_BASE_URL", true, false);

export const PlatformBaseApiNoAuth = async () =>
  await AppApiInstance("PLATFORM_BASE_URL", false, false);

export const PnoeBaseApi = async () =>
  await AppApiInstance("PNOE_API_BASE_URL", true, false);

export const PnoeAgentApi = async () =>
  await AppApiInstance("PNOE_API_AGENT_URL", true, false);

export const BreathBaseApi = async () =>
  await AppApiInstance("BREATH_API_BASE_URL", true, false);

// Signs with the selected client's token (per-tab customer session), so the
// breath service scopes the request to that client exactly as it does for a
// logged-in customer.
export const BreathBaseApiAffiliateRequest = async () =>
  await AppApiInstance("BREATH_API_BASE_URL", false, true);
